import { v4 as generateUuid } from 'uuid'

import { hashPrefixed } from '../../../lib/utils'
import { toPlacementAttribute } from '../combination-rules/placementIds'
import { EMBROIDERY_VERSION_V2 } from '../embroideryVersion'
import { EmbroideryItem } from '../providers/types'
import {
  EmbroideryItemCustomProperties,
  EmbroideryV2GarmentCustomProperties,
  EmbroideryV2ItemCustomProperties,
} from '../types'

import {
  deriveLogoManualHandling,
  removeUnusedProperties,
} from './generateEmbroideryLineItemProperties'

export const EMBROIDERY_PRICING_SIBLING = 'sibling'

/** Keys outside the v2 spec that v2 lines keep for the cart UI, removal logic and downstream. */
export const V2_LEGACY_RETAINED_KEYS = [
  '_LPROP',
  '_embroidery_version',
  '_embroideryId',
  '_scrub_color',
  '_text_thread_color_value',
  '_text_font_family_value',
  '_icon_file_name',
  '_icon_image_source',
  '_logo_id',
  '_logo_file_name',
  '_logo_preview',
  'logo_name',
  'logo_color_option',
  'logo_note',
  '_logo_manual_handling',
  '_logo_setup_embroideryId',
  '_logo_setup_guid',
] as const satisfies readonly (keyof EmbroideryItemCustomProperties)[]

export function generateV2GarmentProperties({
  guid,
  logoId,
}: {
  guid: string
  logoId?: string
}): EmbroideryV2GarmentCustomProperties {
  return removeUnusedProperties<EmbroideryV2GarmentCustomProperties>({
    _guid: guid,
    _embroidery_version: EMBROIDERY_VERSION_V2,
    _embroidery_pricing: EMBROIDERY_PRICING_SIBLING,
    // legacy-retained
    _LPROP: 'final sale',
    _logo_id: logoId,
  })
}

export function generateV2AttachmentProperties(
  item: EmbroideryItem,
  {
    guid,
    productColorHex,
    embroideryVariantId,
    logoSetupVariantId,
  }: {
    guid: string
    productColorHex: string | undefined
    embroideryVariantId: number
    logoSetupVariantId?: number
  }
): EmbroideryV2ItemCustomProperties {
  const location = toPlacementAttribute(item.placementId)

  return removeUnusedProperties<EmbroideryV2ItemCustomProperties>({
    _guid: guid,
    _attachment_id: `att_${item.instanceId}`,
    _type: item.typeKey,
    _location: location,
    ...typeProperties(item, location, logoSetupVariantId),
    // legacy-retained
    _embroidery_version: EMBROIDERY_VERSION_V2,
    _LPROP: 'final sale',
    _embroideryId: embroideryVariantId.toString(),
    _scrub_color: hashPrefixed(productColorHex),
  })
}

function typeProperties(
  item: EmbroideryItem,
  location: string,
  logoSetupVariantId: number | undefined
): EmbroideryV2ItemCustomProperties {
  switch (item.typeKey) {
    case 'text': {
      const { firstLine, secondLine, font, threadColor } = item.values
      return {
        text_first_line: firstLine,
        text_second_line: secondLine,
        text_font: font?.key,
        text_color: threadColor?.designFile,
        text_placement: location,
        // legacy-retained
        _text_thread_color_value: threadColor?.hexCode,
        _text_font_family_value: font?.designFile,
      }
    }
    case 'icon': {
      // v2 icons carry no initials; those are a separate text item on a sleeve.
      const { icon } = item.values
      return {
        // Same sources the legacy icon form uses for iconDesignName/File/ImageSource.
        icon_design: icon?.image.label || undefined,
        icon_placement: location,
        // legacy-retained
        _icon_file_name: icon?.designFile || undefined,
        _icon_image_source: icon?.image.src || undefined,
      }
    }
    case 'logo': {
      const { logoFile, requiresSetup } = item.values
      const setupVariantId = requiresSetup ? logoSetupVariantId : undefined
      return {
        logo_placement: location,
        // legacy-retained; mapped from the LogoFile as the DTC logo form does
        _logo_id: logoFile?.id,
        _logo_file_name: logoFile?.filename ?? undefined,
        _logo_preview: logoFile?.preview,
        logo_name: logoFile?.name,
        logo_color_option: logoFile?.digitizationSetting,
        logo_note: logoFile?.notes ?? undefined,
        _logo_manual_handling: deriveLogoManualHandling(undefined, logoFile),
        _logo_setup_embroideryId: setupVariantId?.toString(),
        _logo_setup_guid: setupVariantId ? generateUuid() : undefined,
      }
    }
  }
}
